// filepath: apps/web/src/features/promote/IrreversibilityWarning.tsx
//
// FR-UI-PROM-1 AC-2: prominently mark field-promotion contract operations
// as irreversible. FR-ENT-4: dual_write → column_only cannot be rolled back
// by pointer rollback.
//
// INV-9 compliant: FieldPromotionContractStep.reason is rendered as a text node only.

import React from "react";
import type { FieldPromotionContractStep } from "./types";

interface IrreversibilityWarningProps {
  contractSteps: FieldPromotionContractStep[];
}

/**
 * Renders a warning banner for irreversible field-promotion contract steps.
 * Each step's reason is rendered as plain text (INV-9).
 */
export function IrreversibilityWarning({
  contractSteps,
}: IrreversibilityWarningProps): React.ReactElement {
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 mb-2"
      data-testid="irreversibility-warning"
      role="alert"
    >
      {/* SVG warning triangle — no external icon library dependency */}
      <svg
        className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M8.257 3.049c.367-.527.916-.843 1.464-.843.549 0 1.098.316 1.464.843l7.128 11.538a1.754 1.754 0 01-.659 2.392 1.754 1.754 0 01-2.393-.659L8.257 3.05zm1.493.85a.75.75 0 00-.014.009L.012 15.386a.75.75 0 00.282.573l.014.01a.75.75 0 00.573.282l15.386.012a.75.75 0 00.573-.282l.01-.014a.75.75 0 00-.282-.573l-7.128-11.538a.75.75 0 00-.575-.282z"
          clipRule="evenodd"
        />
      </svg>
      <div className="text-sm">
        <p className="font-semibold text-destructive">Irreversible Operation</p>
        <p className="text-destructive/80">
          This promotion includes field-promotion contract steps that cannot be
          rolled back by pointer rollback. Once applied, the column-only
          placement is permanent.
        </p>
        <ul className="mt-1 space-y-0.5">
          {contractSteps.map((step, i) => (
            <li key={i} className="text-xs text-destructive/80">
              {/* INV-9: reason rendered as text, not HTML */}
              {step.entity_type}.{step.field_name}
              {step.reason ? ` — ${step.reason}` : ""}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
